import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, AlertTriangle, Phone, MapPin, Clock, Megaphone, BadgeCheck,
  Bell, BellOff, Share2, Lock, Unlock, Pencil, HandHeart, HelpingHand, CheckCircle2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import useAuthorityProfile from '../hooks/useAuthorityProfile';
import { useHubT } from '../components/safetyhub/hubStrings';
import { TYPE_ICONS, STATUS_STYLES, STATUS_DOTS, SEVERITY_STYLES, timeAgo } from '../components/safetyhub/IncidentCard';
import CommunityReportsFeed from '../components/safetyhub/CommunityReportsFeed';
import RemovedPostNotice from '../components/safetyhub/RemovedPostNotice';
import ReportComposer from '../components/safetyhub/ReportComposer';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import NotificationBell from '../components/NotificationBell';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const UPDATE_STYLES = {
  evacuation_order: 'border-red-500 bg-red-50',
  emergency_instructions: 'border-red-400 bg-red-50',
  road_closure: 'border-amber-400 bg-amber-50',
  shelter: 'border-blue-400 bg-blue-50',
  safe_route: 'border-emerald-400 bg-emerald-50',
  official_update: 'border-slate-300 bg-white',
};

export default function IncidentRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const hubT = useHubT();
  const { user } = useAuth();
  const authority = useAuthorityProfile();
  const { ensureSubscription } = useNotifications();

  const [incident, setIncident] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [composerCategory, setComposerCategory] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateType, setUpdateType] = useState('official_update');
  const [updateBody, setUpdateBody] = useState('');
  const [updateBusy, setUpdateBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const canManage = user?.role === 'admin' || authority.isOfficial;

  const load = useCallback(async () => {
    try {
      const [inc, upd] = await Promise.all([
        axios.get(`${API}/incidents/${id}`),
        axios.get(`${API}/incidents/${id}/updates`),
      ]);
      setIncident(inc.data.incident);
      setUpdates(upd.data.updates || []);
    } catch {
      toast.error('Item not found');
      navigate('/safety-bulletin');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const subId = localStorage.getItem('find2day_sub_id');
    if (!subId) return;
    axios.get(`${API}/incidents/follows/${subId}`)
      .then(r => setFollowing((r.data.incidentIds || []).includes(id)))
      .catch(() => {});
  }, [id]);

  // Deep-linked composer (?compose=help_needed / help_offered / report)
  useEffect(() => {
    const compose = searchParams.get('compose');
    if (compose) {
      setComposerCategory(compose === 'report' ? 'general_update' : compose);
      searchParams.delete('compose');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFollow = async () => {
    setFollowBusy(true);
    try {
      const subId = localStorage.getItem('find2day_sub_id') || await ensureSubscription();
      const r = await axios.post(`${API}/incidents/${id}/follow`, { subscriptionId: subId },
        user?.token ? { headers: { Authorization: `Bearer ${user.token}` } } : {});
      setFollowing(r.data.following);
      toast.success(r.data.following ? hubT('followingIncident') : 'OK');
      load();
    } catch {
      toast.error('Could not update');
    } finally {
      setFollowBusy(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/incidents/${id}`;
    if (navigator.share) {
      try { await navigator.share({ title: incident?.title, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(i18n.language === 'es' ? 'Enlace copiado' : 'Link copied');
    } catch { /* noop */ }
  };

  const postUpdate = async () => {
    setUpdateBusy(true);
    try {
      await axios.post(`${API}/incidents/${id}/updates`,
        { updateType, body: updateBody },
        { headers: { Authorization: `Bearer ${user.token}` } });
      toast.success(hubT('sent'));
      setUpdateOpen(false); setUpdateBody('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setUpdateBusy(false);
    }
  };

  const changeStatus = async (newStatus) => {
    setStatusBusy(true);
    try {
      await axios.put(`${API}/incidents/${id}`, { status: newStatus },
        { headers: { Authorization: `Bearer ${user.token}` } });
      toast.success(hubT(`status_${newStatus}`));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally {
      setStatusBusy(false);
    }
  };

  const toggleLock = async () => {
    try {
      await axios.post(`${API}/incidents/${id}/lock`, { locked: !incident.locked },
        { headers: { Authorization: `Bearer ${user.token}` } });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  if (loading || !incident) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400">…</div>;
  }

  const Icon = TYPE_ICONS[incident.incidentType] || AlertTriangle;
  const isResolved = incident.status === 'resolved';

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="incident-room-page">
      <header className={`text-white shadow-lg ${isResolved ? 'bg-gradient-to-r from-emerald-700 to-emerald-600' : 'bg-gradient-to-r from-red-800 to-red-700'}`}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate('/safety-bulletin?tab=incidents')} className="p-2 rounded-lg hover:bg-white/20" data-testid="incident-back-btn">
              <ArrowLeft className="h-5 w-5 rtl-flip" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold flex items-center gap-2 truncate">
                <Icon className="h-5 w-5 shrink-0" /> {incident.title}
              </h1>
              <p className="text-xs text-white/80">{hubT(`it_${incident.incidentType}`)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell variant="on-purple" />
            <LanguageToggle />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-5">
        <RemovedPostNotice />
        {isResolved && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-3 mb-4 flex items-center gap-2 text-emerald-900 text-sm font-semibold" data-testid="incident-resolved-banner">
            <CheckCircle2 className="h-5 w-5" /> {hubT('resolvedBanner')}
          </div>
        )}

        {/* Incident header card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-5" data-testid="incident-header-card">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold ring-1 ${STATUS_STYLES[incident.status]}`}>
              <span className={`h-2 w-2 rounded-full ${STATUS_DOTS[incident.status] || STATUS_DOTS.archived}`} />
              {hubT(`status_${incident.status}`)}
            </span>
            {incident.severity !== 'normal' && (
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold ${SEVERITY_STYLES[incident.severity]}`}>
                {hubT(`sev_${incident.severity}`)}
              </span>
            )}
            <span className="text-xs text-gray-500 inline-flex items-center gap-1 ml-auto">
              <Clock className="h-3 w-3" />
              {incident.lastOfficialUpdateAt
                ? `${hubT('lastOfficialUpdate')}: ${timeAgo(incident.lastOfficialUpdateAt, i18n.language)}`
                : `${hubT('updated')} ${timeAgo(incident.updatedAt || incident.createdAt, i18n.language)}`}
            </span>
          </div>
          <div className="mb-3" data-testid="incident-whats-happening">
            <h2 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
              {hubT('whatsHappening')}
            </h2>
            {(incident.description || incident.summary) ? (
              <>
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid="incident-summary-body">
                  {incident.description || incident.summary}
                </p>
                {!incident.description && incident.summarySource && incident.summarySource !== 'description' && (
                  <p className="text-[11px] text-gray-500 italic mt-1" data-testid="incident-summary-source">
                    {incident.summarySource === 'community_report'
                      ? hubT('summaryFromReport')
                      : hubT('summaryFromUpdate')}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-lg p-3" data-testid="incident-summary-empty">
                {hubT('noCurrentInfo')}
              </p>
            )}
          </div>
          <div className="flex items-start gap-2 text-xs text-gray-600 mb-2">
            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span><span className="font-semibold">{hubT('areasAffected')}:</span> {(incident.areasAffected || []).join(', ')}</span>
          </div>
          {incident.createdByRole !== 'system' && incident.createdByName && (
            <div className="flex items-center gap-1 text-xs text-gray-600 mb-3">
              <BadgeCheck className="h-3.5 w-3.5 text-blue-600" />
              {hubT('postedBy')} <span className="font-semibold text-blue-700">{incident.createdByName}</span>
            </div>
          )}

          {(incident.contactNumbers || []).length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-700 mb-1.5">{hubT('officialContacts')}</p>
              <div className="flex flex-wrap gap-2">
                {incident.contactNumbers.map((c, i) => (
                  <a
                    key={i}
                    href={`tel:${c.number.replace(/\s/g, '')}`}
                    className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-slate-900"
                    data-testid={`incident-contact-${i}`}
                  >
                    <Phone className="h-3 w-3" />{c.label}: {c.number}
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 pt-3">
            <button
              onClick={toggleFollow}
              disabled={followBusy}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                following
                  ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
              data-testid="incident-follow-btn"
            >
              {following ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
              {following ? hubT('followingIncident') : hubT('followIncident')}
              {incident.followerCount > 0 ? ` · ${incident.followerCount}` : ''}
            </button>
            <button
              onClick={share}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-slate-100 text-slate-700 hover:bg-slate-200"
              data-testid="incident-share-btn"
            >
              <Share2 className="h-3.5 w-3.5" />{hubT('share')}
            </button>
            <a
              href="tel:112"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 ring-1 ring-amber-300 hover:bg-amber-200"
              data-testid="incident-call-112"
            >
              <Phone className="h-3.5 w-3.5" />{hubT('call112')}
            </a>
          </div>

          {canManage && (
            <div className="flex items-center gap-2 flex-wrap border-t border-gray-100 pt-3 mt-3" data-testid="incident-manage-bar">
              <select
                value={incident.status}
                onChange={e => changeStatus(e.target.value)}
                disabled={statusBusy}
                className="border rounded-md px-2 py-1 text-xs bg-white"
                data-testid="incident-status-select"
              >
                {['active', 'monitoring', 'resolved', 'archived'].map(s => (
                  <option key={s} value={s}>{hubT(`status_${s}`)}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={toggleLock} data-testid="incident-lock-btn">
                {incident.locked ? <Unlock className="h-3.5 w-3.5 mr-1" /> : <Lock className="h-3.5 w-3.5 mr-1" />}
                {incident.locked ? hubT('unlockRoom') : hubT('lockRoom')}
              </Button>
              <Button
                size="sm"
                onClick={() => setUpdateOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white ml-auto"
                data-testid="incident-post-update-btn"
              >
                <Megaphone className="h-3.5 w-3.5 mr-1" />{hubT('postUpdate')}
              </Button>
            </div>
          )}
        </div>

        {/* Help quick actions */}
        {!isResolved && (
          <div className="grid grid-cols-2 gap-2 mb-5">
            <button
              onClick={() => setComposerCategory('help_needed')}
              className="flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold bg-rose-600 text-white shadow hover:bg-rose-700"
              data-testid="incident-request-help-btn"
            >
              <HandHeart className="h-4 w-4" />{hubT('requestHelp')}
            </button>
            <button
              onClick={() => setComposerCategory('help_offered')}
              className="flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold bg-emerald-600 text-white shadow hover:bg-emerald-700"
              data-testid="incident-offer-help-btn"
            >
              <HelpingHand className="h-4 w-4" />{hubT('offerHelp')}
            </button>
          </div>
        )}

        {/* Official updates — pinned above community content (hidden when a
            community-created group has none) */}
        {(updates.length > 0 || canManage) && (
        <section id="updates" className="mb-6" data-testid="incident-official-updates">
          <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-red-700" /> {hubT('officialUpdates')}
          </h2>
          {updates.length === 0 ? (
            <p className="text-sm text-gray-500">—</p>
          ) : (
            <div className="space-y-2">
              {updates.map(u => (
                <div
                  key={u.id}
                  className={`rounded-xl border-l-4 border p-3 shadow-sm ${UPDATE_STYLES[u.updateType] || UPDATE_STYLES.official_update}`}
                  data-testid={`official-update-${u.id}`}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-wide text-gray-800">
                      <BadgeCheck className="h-3.5 w-3.5 text-blue-600" />
                      {hubT('official')} · {hubT(`ut_${u.updateType}`)}
                    </span>
                    <span className="text-[11px] text-gray-500 ml-auto">{timeAgo(u.createdAt, i18n.language)}</span>
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{u.body}</p>
                  <p className="text-[11px] text-gray-500 mt-1">{u.authorName}</p>
                </div>
              ))}
            </div>
          )}
        </section>
        )}

        {/* Community reports for this incident */}
        <section data-testid="incident-community-section">
          <h2 className="text-base font-bold text-gray-900 mb-3">{hubT('communityReports')}</h2>
          <CommunityReportsFeed
            incidentId={id}
            defaultTown={(incident.areasAffected || [])[0] || ''}
            locked={incident.locked && user?.role !== 'admin'}
          />
        </section>
      </main>

      {/* Composer for deep-linked help / report */}
      <ReportComposer
        open={!!composerCategory}
        onClose={() => setComposerCategory(null)}
        incidentId={id}
        defaultCategory={composerCategory || 'general_update'}
        defaultTown={(incident.areasAffected || [])[0] || ''}
        onPosted={load}
      />

      {/* Official update composer */}
      <Dialog open={updateOpen} onOpenChange={(o) => { if (!o) setUpdateOpen(false); }}>
        <DialogContent data-testid="official-update-dialog">
          <DialogHeader><DialogTitle>{hubT('postUpdate')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select
              value={updateType}
              onChange={e => setUpdateType(e.target.value)}
              className="w-full border rounded-md px-2 py-2 text-sm bg-white"
              data-testid="official-update-type-select"
            >
              {['official_update', 'evacuation_order', 'road_closure', 'shelter', 'emergency_instructions', 'safe_route'].map(u => (
                <option key={u} value={u}>{hubT(`ut_${u}`)}</option>
              ))}
            </select>
            <Textarea
              value={updateBody}
              onChange={e => setUpdateBody(e.target.value)}
              rows={4}
              maxLength={600}
              placeholder={hubT('describe')}
              data-testid="official-update-body-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateOpen(false)}>{hubT('cancel')}</Button>
            <Button
              onClick={postUpdate}
              disabled={updateBusy || updateBody.trim().length < 10}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="official-update-submit"
            >
              {updateBusy ? '…' : hubT('submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNavigation />
    </div>
  );
}
